import { placeAnchoredPopover } from './table-anchored-popover.mjs?v=20260806.100';

export function createOverlayManager({ documentRef = document, onAfterClose = () => {} } = {}) {
  if (!documentRef?.body || typeof documentRef.createElement !== 'function') {
    throw new TypeError('Overlay document is invalid.');
  }

  let overlay = null;
  let anchor = null;
  let modal = false;
  let closeHook = null;
  let resizeObserver = null;

  function removeListeners() {
    documentRef.removeEventListener('keydown', onKeyDown, true);
    documentRef.removeEventListener('pointerdown', onPointerDown, true);
    documentRef.removeEventListener('scroll', reposition, true);
    documentRef.defaultView?.removeEventListener?.('resize', reposition);
    resizeObserver?.disconnect?.();
    resizeObserver = null;
  }

  function close(reason = 'dismiss') {
    if (!overlay) return;
    const focusTarget = anchor;
    const closingOverlay = overlay;
    const callback = closeHook;
    closeHook = null;
    overlay = null;
    if (focusTarget?.setAttribute) focusTarget.setAttribute('aria-expanded', 'false');
    anchor = null;
    modal = false;
    removeListeners();
    try {
      if (typeof callback === 'function') callback(reason, closingOverlay);
    } finally {
      closingOverlay.remove();
      onAfterClose(reason);
    }
    if (reason !== 'replace' && focusTarget?.focus) focusTarget.focus();
  }

  function onKeyDown(event) {
    if (event.key === 'Escape') close('escape');
  }

  function onPointerDown(event) {
    if (!overlay || modal) return;
    const target = event.target;
    if (overlay.contains?.(target) || anchor?.contains?.(target)) return;
    close('outside');
  }

  function reposition() {
    if (!overlay || modal || !anchor?.getBoundingClientRect) return;
    const anchorBounds = anchor.getBoundingClientRect();
    const overlayBounds = overlay.getBoundingClientRect?.() || {};
    const viewport = {
      width: documentRef.documentElement?.clientWidth || 1440,
      height: documentRef.documentElement?.clientHeight || 900,
    };
    const geometry = placeAnchoredPopover({
      anchor: anchorBounds,
      overlay: {
        width: overlayBounds.width || Math.min(360, viewport.width - 24),
        height: overlayBounds.height || Math.min(240, viewport.height - 24),
      },
      viewport,
    });
    overlay.dataset.placement = geometry.placement;
    overlay.style.setProperty('--dashboard-overlay-left', `${geometry.left}px`);
    overlay.style.setProperty('--dashboard-overlay-top', `${geometry.top}px`);
    overlay.style.setProperty('--dashboard-overlay-arrow-x', `${geometry.arrowX}px`);
  }

  function open(options = {}) {
    close('replace');
    anchor = options.anchor ?? null;
    modal = options.modal === true;
    closeHook = typeof options.onClose === 'function' ? options.onClose : null;
    overlay = documentRef.createElement('div');
    overlay.className = modal
      ? 'dashboard-overlay-layer dashboard-overlay dashboard-overlay--modal'
      : 'dashboard-overlay-layer dashboard-overlay dashboard-overlay--popover';
    overlay.dataset.overlay = options.kind || 'dashboard';
    const overlayRole = modal || options.role === 'dialog' ? 'dialog' : 'region';
    overlay.setAttribute('role', overlayRole);
    if (typeof options.ariaLabel === 'string' && options.ariaLabel.trim()) {
      overlay.setAttribute('aria-label', options.ariaLabel.trim());
    }
    if (modal) overlay.setAttribute('aria-modal', 'true');
    if (typeof options.content === 'string') overlay.innerHTML = options.content;
    else if (options.content) overlay.append(options.content);
    if (anchor?.setAttribute) anchor.setAttribute('aria-expanded', 'true');
    documentRef.body.append(overlay);
    reposition();
    const ResizeObserverClass = documentRef.defaultView?.ResizeObserver || globalThis.ResizeObserver;
    if (!modal && typeof ResizeObserverClass === 'function') {
      resizeObserver = new ResizeObserverClass(reposition);
      resizeObserver.observe(overlay);
    }
    documentRef.addEventListener('keydown', onKeyDown, true);
    documentRef.addEventListener('pointerdown', onPointerDown, true);
    documentRef.addEventListener('scroll', reposition, true);
    documentRef.defaultView?.addEventListener?.('resize', reposition);
    return overlay;
  }

  return {
    open,
    close,
    isOpen: () => overlay !== null,
    element: () => overlay,
    destroy() { close('destroy'); },
  };
}
